/**
 * AstroFormPage — the full-screen, mandatory birth-details form.
 *
 * Rendered by HoroscopePage when the feed config says shouldShowAstroForm. It
 * owns its own form state (useState; no store — the data lives only until submit,
 * like AccountDeletion). The page-level back button + close/lifecycle stay in
 * HoroscopePage (shared across the form and the feed), so this component is just
 * the form body + fixed CTA + the place-search overlay.
 *
 * On a successful submit it calls `onSubmitted` (= the feed's reload()), which
 * re-fetches /today. With details now saved the backend returns
 * shouldShowAstroForm:false + items, so the coordinator swaps to the feed — the
 * client never hand-builds the transition.
 */
import { useEffect, useState } from 'react'
import { AstroFormMeta } from '../../api/types'
import { postAstroForm } from '../../api/astroForm'
import { logEvent } from '../../lib/analytics'
import { EventAction, EventComponent, EventSection } from '../../lib/eventConstants'
import {
  AstroFormErrors,
  AstroFormValues,
  INITIAL_VALUES,
  formatDob,
  formatTime,
  hasErrors,
  validate,
  validateDob,
  validateName,
  validateTime,
} from './validation'
import { NameField } from './fields/NameField'
import { GenderRadio } from './fields/GenderRadio'
import { DobBoxes, DobField } from './fields/DobBoxes'
import { PlaceOfBirthField } from './fields/PlaceOfBirthField'
import { TimeOfBirthField } from './fields/TimeOfBirthField'
import { PlaceSearchOverlay } from './PlaceSearchOverlay'

interface Props {
  meta: AstroFormMeta
  /** Called after a successful submit — the feed's reload() (re-fetches /today). */
  onSubmitted: () => void
}

export function AstroFormPage({ meta, onSubmitted }: Props) {
  const [values, setValues] = useState<AstroFormValues>(INITIAL_VALUES)
  const [errors, setErrors] = useState<AstroFormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  const [overlayOpen, setOverlayOpen] = useState(false)

  useEffect(() => {
    logEvent({ section: EventSection.ASTRO_FORM, action: EventAction.OPEN })
  }, [])

  // Which error a given value-field belongs to (DOB/time span three inputs each).
  const ERR_KEY: Partial<Record<keyof AstroFormValues, keyof AstroFormErrors>> = {
    name: 'name',
    dobDay: 'dob',
    dobMonth: 'dob',
    dobYear: 'dob',
    birthHours: 'birthTime',
    birthMinutes: 'birthTime',
    birthIsAm: 'birthTime',
    birthPlace: 'birthPlace',
  }

  function set<K extends keyof AstroFormValues>(key: K, value: AstroFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }))
    // Clear that field's error as the user edits it; it's re-checked on blur/submit.
    const errKey = ERR_KEY[key]
    if (errKey) setErrors((prev) => (prev[errKey] ? { ...prev, [errKey]: undefined } : prev))
  }

  // Per-field validation on blur (uses the just-rendered `values`).
  const blurName = () => setErrors((prev) => ({ ...prev, name: validateName(values) }))
  const blurDob = () => setErrors((prev) => ({ ...prev, dob: validateDob(values) }))
  const blurTime = () => setErrors((prev) => ({ ...prev, birthTime: validateTime(values, meta) }))

  const handleSubmit = async () => {
    if (submitting) return
    const validationErrors = validate(values, meta)
    setErrors(validationErrors)
    if (hasErrors(validationErrors)) {
      logEvent({
        section: EventSection.ASTRO_FORM,
        action: EventAction.CLICK,
        component: EventComponent.VALIDATION_ERROR,
        // Only the fields that actually failed — validate() returns every key
        // (undefined when valid), so filter to the ones with a message.
        meta: { fields: Object.keys(validationErrors).filter((k) => validationErrors[k as keyof AstroFormErrors]) },
      })
      return
    }

    setSubmitting(true)
    setSubmitFailed(false)
    try {
      await postAstroForm({
        name: values.name.trim(),
        gender: values.gender,
        dateOfBirth: formatDob(values.dobDay, values.dobMonth, values.dobYear),
        timeOfBirth: formatTime(values),
        placeOfBirth: values.birthPlace,
      })
      logEvent({ section: EventSection.ASTRO_FORM, action: EventAction.CLICK, component: EventComponent.SUBMIT })
      // Re-fetch the feed; normally this component unmounts as the coordinator
      // swaps views. Re-enable the CTA anyway so we never get stuck on
      // "Submitting…" if the server still returns shouldShowAstroForm (lag /
      // eventual consistency) and this same instance stays mounted. The state
      // update is a harmless no-op when we do unmount.
      onSubmitted()
      setSubmitting(false)
    } catch {
      setSubmitting(false)
      setSubmitFailed(true)
    }
  }

  return (
    <>
      {/* pb-28 keeps the last field clear of the fixed CTA; pt for the back button. */}
      <div className="flex min-h-screen flex-col px-4 pb-28 pt-14">
        <h1 className="text-center text-headingSmall text-textPrimary">{meta.title}</h1>

        <div className="mt-8 flex flex-col gap-6">
          <NameField
            value={values.name}
            onChange={(v) => set('name', v)}
            onBlur={blurName}
            error={errors.name}
          />
          <GenderRadio value={values.gender} onChange={(v) => set('gender', v)} />
          <DobBoxes
            day={values.dobDay}
            month={values.dobMonth}
            year={values.dobYear}
            onChange={(field: DobField, v) =>
              set(field === 'day' ? 'dobDay' : field === 'month' ? 'dobMonth' : 'dobYear', v)
            }
            onBlur={blurDob}
            error={errors.dob}
          />
          <PlaceOfBirthField
            value={values.birthPlace}
            required={meta.isBirthPlaceMandatory}
            onOpen={() => {
              setOverlayOpen(true)
              logEvent({
                section: EventSection.ASTRO_FORM,
                action: EventAction.CLICK,
                component: EventComponent.PLACE_SEARCH,
              })
            }}
            error={errors.birthPlace}
          />
          <TimeOfBirthField
            hours={values.birthHours}
            minutes={values.birthMinutes}
            isAm={values.birthIsAm}
            required={meta.isBirthTimeMandatory}
            onChange={(field, v) =>
              set(
                field === 'hours' ? 'birthHours' : field === 'minutes' ? 'birthMinutes' : 'birthIsAm',
                v as never
              )
            }
            onBlur={blurTime}
            error={errors.birthTime}
          />

          {submitFailed && (
            <p className="text-bodySmall text-errorPrimary">
              Something went wrong. Please try again.
            </p>
          )}
        </div>
      </div>

      {/* Fixed-bottom CTA (centered within the page's max width). */}
      <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-[600px] bg-surfaceZero px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="h-12 w-full rounded-full bg-accentPrimary text-labelLarge text-textInversePrimary disabled:opacity-60"
        >
          {submitting ? 'Submitting…' : meta.ctaText}
        </button>
      </div>

      {overlayOpen && (
        <PlaceSearchOverlay
          onClose={() => setOverlayOpen(false)}
          onSelect={(city) => {
            set('birthPlace', city)
            setOverlayOpen(false)
            setErrors((prev) => ({ ...prev, birthPlace: undefined }))
            logEvent({
              section: EventSection.ASTRO_FORM,
              action: EventAction.CLICK,
              component: EventComponent.PLACE_SELECTED,
            })
          }}
        />
      )}
    </>
  )
}

export default AstroFormPage
